/**
 * Analysis Detail Page
 * Displays full analysis results with accordions
 */

import React from 'react';
import { useParams, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { Accordion } from '@/components/common/Accordion';
import { SentimentCard } from '@/components/analysis/SentimentCard';
import { EmotionCard } from '@/components/analysis/EmotionCard';
import { BehavioralTraitsCard } from '@/components/analysis/BehavioralTraitsCard';
import { ClarityAnalysis } from '@/components/analysis/ClarityAnalysis';
import { analysisApi } from '@/services/api';
import type { Analysis } from '@/types/analysis';

const AnalysisDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get analysis data from navigation state (passed from Analyser)
  const passedData = location.state as any;
  
  console.log('📄 AnalysisDetail - ID:', id);
  console.log('📄 AnalysisDetail - Passed data:', passedData);
  
  // Fetch from API if no data was passed
  const { data: fetchedData, isLoading: isFetching, error: fetchError } = useQuery({
    queryKey: ['analysis', id],
    queryFn: async () => {
      const response = await analysisApi.getAnalysis(id!);
      return response.data?.data; // Backend returns { success: true, data: {...} }
    },
    enabled: !passedData && !!id, // Only fetch if we don't have passed data
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Determine which data source to use
  const sourceData = passedData || fetchedData;
  const isLoading = !passedData && isFetching;
  const error = !passedData && fetchError;
  
  console.log('📊 Source data:', sourceData);
  console.log('⏳ Loading:', isLoading);
  console.log('❌ Error:', error);
  
  // Log trait_analysis availability
  if (fetchedData) {
    console.log('🎯 AnalysisDetail - trait_analysis from API:', fetchedData.trait_analysis || 'NOT FOUND (legacy doc)');
  }
  if (passedData) {
    console.log('🎯 AnalysisDetail - traits from SSE:', passedData.traits || 'NOT FOUND');
  }
  
  // Map data to Analysis type
  const data: Analysis | null = sourceData ? (passedData ? {
    // Mapping for passed data from navigation state
    _id: id!,
    lite_doc_id: id!,
    text: passedData.originalText || '',
    context: passedData.context || '',
    date: new Date().toISOString(),
    user_id: '', // Not needed for display
    org_id: '', // Not needed for display
    sentiment: passedData.sentiment ? {
      label: passedData.sentiment.positivity > passedData.sentiment.negativity ? 'positive' : 
             passedData.sentiment.negativity > passedData.sentiment.positivity ? 'negative' : 'neutral',
      score: Math.max(passedData.sentiment.positivity, passedData.sentiment.negativity, passedData.sentiment.neutral),
    } : { label: 'neutral', score: 0 },
    emotions: passedData.emotions ? Object.entries(passedData.emotions)
      .map(([emotion, score]: [string, any]) => ({
        emotion: emotion.replace(/_rating$/, '').replace(/_/g, ' '),
        score: score,
      }))
      .filter((e: any) => e.score > 0.2)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 10) : [],
    // Pass raw traits object - BehavioralTraitsCard will parse score+category pairs
    traits: passedData.traits || {},
    clarity_analysis: passedData.clarity,
    ai_explanation: passedData.explanation,
    metadata: passedData.metadata || undefined,
  } : {
    // Mapping for fetched data from API (MongoDB document)
    _id: fetchedData._id,
    lite_doc_id: fetchedData._id,
    text: fetchedData.result?.input || fetchedData.text || '',
    context: fetchedData.context || '',
    date: fetchedData.date,
    user_id: fetchedData.user_id,
    org_id: fetchedData.org_id,
    metadata: fetchedData.metadata || undefined,
    sentiment: fetchedData.result?.analysis?.sentiment ? {
      label: fetchedData.result.analysis.sentiment.positivity > fetchedData.result.analysis.sentiment.negativity ? 'positive' : 
             fetchedData.result.analysis.sentiment.negativity > fetchedData.result.analysis.sentiment.positivity ? 'negative' : 'neutral',
      score: Math.max(fetchedData.result.analysis.sentiment.positivity, fetchedData.result.analysis.sentiment.negativity, fetchedData.result.analysis.sentiment.neutral),
    } : { label: 'neutral', score: 0 },
    emotions: fetchedData.result?.analysis?.emotion ? Object.entries(fetchedData.result.analysis.emotion)
      .map(([emotion, score]: [string, any]) => ({
        emotion: emotion.replace(/_rating$/, '').replace(/_/g, ' '),
        score: score,
      }))
      .filter((e: any) => e.score > 0.2)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, 10) : [],
    // Traits: Use trait_analysis from MongoDB if available (new docs), 
    // otherwise empty object (legacy docs). BehavioralTraitsCard will handle both.
    traits: fetchedData.trait_analysis || {},
    clarity_analysis: fetchedData.clarity_analysis,
    ai_explanation: fetchedData.explanation,
  }) : null;

  if (!id) {
    return <Navigate to="/history" replace />;
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner 
            size="lg" 
            message="Loading analysis..." 
            showTimeoutMessage={true}
          />
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-center min-h-[60vh]">
          <ErrorDisplay
            type="not-found"
            title="Analysis Not Found"
            message="The analysis you're looking for doesn't exist or you don't have access to it. It may have been deleted."
            onGoBack={() => navigate('/history')}
            showRetry={false}
            showGoBack={true}
          />
        </div>
      </Layout>
    );
  }

  const analysis = data as Analysis;
  const originalText = analysis.text || analysis.result?.input || '';
  const analysisDate = new Date(analysis.date);

  console.log('🎯 AnalysisDetail - Analysis traits:', analysis.traits);
  console.log('🎯 AnalysisDetail - Data source:', passedData ? 'Navigation state (SSE)' : 'API fetch (MongoDB)');

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link
            to="/history"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="material-symbols-outlined text-xl mr-1">
              arrow_back
            </span>
            Back to History
          </Link>
        </div>

        {/* Page Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 px-4">
              Communication Analysis Results
            </h1>
            {/* Email Source Badge */}
            {analysis.metadata?.source === 'email' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <span className="material-symbols-outlined text-sm">
                  mail
                </span>
                Email
              </span>
            )}
          </div>
          
          {/* Email Subject */}
          {analysis.metadata?.source === 'email' && analysis.metadata?.subject && (
            <p className="text-lg sm:text-xl font-medium text-gray-700 mb-2 px-4 italic">
              "{analysis.metadata.subject}"
            </p>
          )}
          
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4">
            {analysis.metadata?.source === 'email' && analysis.metadata?.original_date ? (
              <>
                Email sent on {new Date(analysis.metadata.original_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                <br />
                <span className="text-xs text-gray-500">
                  Analysed on {analysisDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </>
            ) : (
              <>
                Analysis performed on {analysisDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </>
            )}
          </p>
        </div>

        {/* Original Message */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Original Message
          </label>
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">
              {originalText}
            </pre>
          </div>
        </div>

        {/* Analysis Sections */}
        <div className="space-y-6">
          {/* Response Analysis Accordion */}
          <Accordion
            title="Response Analysis"
            defaultOpen={true}
            icon={
              <span className="material-symbols-outlined text-blue-500">
                analytics
              </span>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
              <SentimentCard sentiment={analysis.sentiment} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EmotionCard emotions={analysis.emotions} />
              <BehavioralTraitsCard traits={analysis.traits} />
            </div>
          </Accordion>

          {/* Communication Clarity Accordion */}
          {analysis.clarity_analysis && (
            <Accordion
              title="Communication Clarity"
              icon={
                <span className="material-symbols-outlined text-purple-500">
                  visibility
                </span>
              }
            >
              <ClarityAnalysis clarity={analysis.clarity_analysis} />
            </Accordion>
          )}

          {/* AI Explanation Accordion */}
          {analysis.ai_explanation && (
            <Accordion
              title="AI Explanation"
              icon={
                <span className="material-symbols-outlined text-primary-500">
                  psychology
                </span>
              }
            >
              <div className="prose prose-sm max-w-none [&_ul]:space-y-3 [&_ol]:space-y-3 [&_li]:leading-relaxed [&_p]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-gray-200">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {analysis.ai_explanation}
                </ReactMarkdown>
              </div>
            </Accordion>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/analyse"
            className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            <span className="material-symbols-outlined mr-2">
              psychology
            </span>
            Analyse New Communication
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default AnalysisDetail;
